import _FormControlLabel from "@mui/material/FormControlLabel";
import _Checkbox from "@mui/material/Checkbox";
import _FormControl from "@mui/material/FormControl";
import _Select from "@mui/material/Select";
import _MenuItem from "@mui/material/MenuItem";
import _Chip from "@mui/material/Chip";
import _Typography from "@mui/material/Typography";
import _Add from "@mui/icons-material/Add";
import _Tooltip from "@mui/material/Tooltip";
import _IconButton from "@mui/material/IconButton";
import _Box from "@mui/material/Box";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import { lighten } from '@mui/material/styles';
import { LngLatBounds } from 'maplibre-gl';
import { useSnackbar } from 'notistack';
import React, { Fragment, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Trans, useTranslation } from 'react-i18next';
import { facilityDistributionMinZoom, useAmplitudeTracker, useCancellablePromise, useRouting } from '../../hooks';
import { cyclingProfiles, pavedOnlyBikeTypes } from '../../models';
import { ComputedRouteService, GeocoderService, UserService } from '../../services';
import { getRoutePreview, toBounds } from '../../utils';
import { CargoBikeIcon, HybridBikeIcon, OwnBikeIcon, SharedBikeIcon, SwapIcon } from '../icons';
import { WayPointRow } from './way-point-row';
import { WayPointsList } from './way-points-list';
const bikeTypes = [{
  Icon: OwnBikeIcon,
  key: 'own',
  labelKey: 'commons.routing.form.bike_types.own'
}, {
  Icon: SharedBikeIcon,
  key: 'shared',
  labelKey: 'commons.routing.form.bike_types.shared'
}, {
  Icon: CargoBikeIcon,
  key: 'cargo',
  labelKey: 'commons.routing.form.bike_types.cargo',
  status: cyclingProfiles.cargo?.status
}, {
  Icon: HybridBikeIcon,
  key: 'hybridBike',
  labelKey: 'commons.routing.form.bike_types.hybrid_bike',
  status: cyclingProfiles.hybridBike?.status
}];
function _RoutingForm({
  theme,
  size,
  readOnly,
  map,
  mapPadding,
  zoom,
  mapClickDisabled,
  mapInteractionsDisabled,
  disableMapAnimation,
  defaultOptions,
  disableRideItinerary,
  disableBikeType,
  disableDistributionMinZoom,
  disableEBike,
  disableNewStepAddition,
  newStepPosition,
  disableTooltip,
  retrieveElevations,
  enableSingleResult,
  enableCyclingQualities,
  disableOtherResultsForHybridBike,
  geolocation,
  userPlaces,
  roadsDistribution,
  search,
  wayPoints,
  carRoute,
  computedRoutes,
  selectedComputedRouteIndex,
  setWayPoints,
  selectComputedRouteIndex,
  setComputedRoutes,
  onContextMenuOpen,
  onResponse,
  onUpdate
}, ref) {
  const {
    trackEvent
  } = useAmplitudeTracker();
  const [facilityDistributionEnabled, enableFacilityDistribution] = useState(false);
  const [bikeType, setBikeType] = useState(search.bikeType);
  const [eBikeEnabled, enableEBike] = useState(search.eBikeEnabled);
  const [asphaltOnly, setAsphaltOnly] = useState(search.asphaltOnly);
  const {
    t
  } = useTranslation();
  const snackbar = useSnackbar();
  const {
    init: initMap,
    updateWayPoints: updateWayPointsOnMap,
    showPreview: showPreviewOnMap,
    showCarRoute: showCarRouteOnMap,
    showRoutes: showRoutesOnMap,
    clearRoutes: clearRoutesOnMap,
    destroy: destroyMap
  } = useRouting(theme, map, search, {}, {
    disableNewStepAddition,
    hidePopup: disableTooltip,
    readOnly,
    onSearchUpdated: handleWayPointUpdatedOnMap,
    onRouteSelected: index => {
      const route = computedRoutes?.[index];
      if (route && route.cyclingProfile) search.profile = route.cyclingProfile;
      selectComputedRouteIndex?.(index);
    },
    onContextMenuOpen
  }, !mapInteractionsDisabled && !mapClickDisabled);
  const {
    cancellablePromise,
    cancelPromises
  } = useCancellablePromise();
  useEffect(() => {
    return () => {
      cancelPromises();
    };
  }, []);
  useEffect(() => {
    if (UserService.currentUser && !computedRoutes && bikeType === 'own') {
      const {
        profile,
        pavedOnly
      } = UserService.currentUser.parameters;
      search.profile = profile;
      search.asphaltOnly = pavedOnly;
      setAsphaltOnly(pavedOnly);
    }
  }, [UserService.currentUser]);

  // `search` peut n'etre renseigne qu'apres le montage — parametres d'URL, utilisateur courant —
  // sur une page qui rend d'abord un formulaire vide pour rester hydratable. L'etat local doit
  // alors suivre, sans quoi le type de velo resterait sur sa valeur par defaut.
  useEffect(() => {
    setBikeType(search.bikeType);
    enableEBike(search.eBikeEnabled);
    setAsphaltOnly(search.asphaltOnly);
  }, [search, search.bikeType, search.eBikeEnabled, search.asphaltOnly]);
  useEffect(() => {
    if (map) {
      initMap();
    }
    return () => {
      destroyMap();
    };
  }, [map]);
  useEffect(() => {
    if (zoom) {
      if (facilityDistributionEnabled && zoom < facilityDistributionMinZoom) enableFacilityDistribution(false);else if (!facilityDistributionEnabled && zoom >= facilityDistributionMinZoom) enableFacilityDistribution(true);
    }
  }, [zoom]);
  useEffect(() => {
    if (wayPoints.every(Boolean)) {
      const preview = getRoutePreview(wayPoints);
      const {
        north,
        east,
        south,
        west
      } = toBounds(preview.geometry);
      map?.fitBounds(new LngLatBounds({
        lat: south,
        lng: west
      }, {
        lat: north,
        lng: east
      }), {
        padding: mapPadding,
        animate: !disableMapAnimation
      });
    }
  }, [wayPoints]);
  useEffect(() => {
    if (computedRoutes) {
      showRoutesOnMap(computedRoutes, selectedComputedRouteIndex, {
        enableFacilityDistribution: true,
        disableDistributionMinZoom,
        roadsDistribution
      });
      if (carRoute) showCarRouteOnMap(carRoute, computedRoutes.length, selectedComputedRouteIndex);
    }
  }, [map, computedRoutes, selectedComputedRouteIndex, facilityDistributionEnabled, carRoute, roadsDistribution, disableDistributionMinZoom]);
  useEffect(() => {
    const computedRoute = computedRoutes?.[selectedComputedRouteIndex];
    if (map && computedRoutes && computedRoute) {
      const {
        bounds
      } = computedRoute;
      if (bounds) {
        const {
          north,
          east,
          south,
          west
        } = bounds;
        map.fitBounds(new LngLatBounds({
          lat: south,
          lng: west
        }, {
          lat: north,
          lng: east
        }), {
          padding: mapPadding,
          animate: !disableMapAnimation
        });
      }
    }
  }, [map, computedRoutes, selectedComputedRouteIndex]);
  useImperativeHandle(ref, () => ({
    updateBikeType,
    updateWayPoints: showPreview => {
      updateWayPoints(showPreview);
      updateWayPointsOnMap(search.wayPoints);
    },
    updateWayPointsOnMap: () => updateWayPointsOnMap(search.wayPoints)
  }));
  async function compute(showPreview = true) {
    cancelPromises();
    selectComputedRouteIndex?.(0);
    if (!search.computable) {
      clearRoutesOnMap();
      setComputedRoutes(null);
      onResponse?.(null);
      return;
    }
    if (showPreview) {
      const preview = getRoutePreview(search.wayPoints);
      showPreviewOnMap(preview);
    }
    setComputedRoutes(undefined);
    onResponse?.(undefined);
    try {
      trackEvent('Route Computed');
      const {
        response,
        computedRoutes
      } = await cancellablePromise(ComputedRouteService.compute(search, {
        showRideItinerary: disableRideItinerary ? false : true,
        bikeParkings: false,
        elevations: retrieveElevations || false,
        facilities: true,
        energy: true,
        geometry: true,
        instructions: true,
        showPushingBikeInstructions: true,
        singleResult: Boolean(enableSingleResult) || disableOtherResultsForHybridBike && search.bikeType === 'hybridBike',
        roadCyclingQualities: enableCyclingQualities || false
      }));
      if (computedRoutes.length === 0) throw new Error('no route');
      const orders = {
        road: 0,
        cargo: 0,
        hybridBike: 0,
        eBike: 0,
        daily: 1,
        safe: 2,
        direct: 3,
        touristic: 4
      };
      computedRoutes.sort((a, b) => {
        if (a.cyclingProfile && b.cyclingProfile) {
          return orders[a.cyclingProfile] - orders[b.cyclingProfile];
        }
        return 0;
      });
      if (search.bikeType === 'own') {
        const index = computedRoutes.findIndex(({
          cyclingProfile
        }) => cyclingProfile === search.profile);
        selectComputedRouteIndex?.(index > -1 ? index : 0);
      }
      setComputedRoutes(computedRoutes);
      onResponse?.(response);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        setComputedRoutes([]);
        onResponse?.([]);
        snackbar?.enqueueSnackbar(t('commons.routing.form.server_error'), {});
      }
    }
  }
  function updateBikeType(_bikeType, defaultProfile = 'daily') {
    const newBikeType = _bikeType === 'road' ? 'own' : _bikeType;
    setBikeType(newBikeType);
    search.bikeType = newBikeType;
    search.profile = newBikeType === 'hybridBike' ? 'hybridBike' : newBikeType === 'cargo' ? 'cargo' : defaultProfile === 'road' ? 'daily' : defaultProfile;
    search.transportModes = newBikeType === 'shared' ? ['bike', 'pedestrian'] : ['bike'];
    if (_bikeType === 'road') {
      setAsphaltOnly(true);
      search.asphaltOnly = true;
    } else if (!pavedOnlyBikeTypes.includes(newBikeType)) {
      setAsphaltOnly(false);
      search.asphaltOnly = false;
    }
    onUpdate?.();
    compute();
  }
  function handleBikeTypeChange({
    target: {
      value
    }
  }) {
    updateBikeType(value);
  }
  function handleEBikeToggle(_, checked) {
    enableEBike(checked);
    search.eBikeEnabled = checked;
    onUpdate?.();
    compute();
  }
  function handleAsphaltOnlyToggle(_, checked) {
    setAsphaltOnly(checked);
    search.asphaltOnly = checked;
    onUpdate?.();
    compute();
  }
  async function updateWayPoints(showPreview) {
    setWayPoints([...search.wayPoints]);
    onUpdate?.();
    compute(showPreview);
    if (!search.wayPoints.find(wayPoint => wayPoint && !wayPoint.addressDetail)) return;
    try {
      const newWayPoints = await cancellablePromise(Promise.all(search.wayPoints.map(wayPoint => wayPoint && !wayPoint.addressDetail ? GeocoderService.reverseGeocode(undefined, wayPoint.point) : wayPoint?.clone())));
      search.wayPoints = newWayPoints;
      setWayPoints(newWayPoints);
    } catch {
      //
    }
  }
  function handleWayPointUpdatedOnMap() {
    updateWayPoints(false);
  }
  function handleWayPointBlur(index) {
    if (index > 1 && !search.wayPoints[index]) {
      const newWayPoints = [...search.wayPoints];
      newWayPoints.splice(index, 1);
      search.wayPoints = newWayPoints;
      setWayPoints(newWayPoints);
    } else {
      setWayPoints([...search.wayPoints]);
    }
  }
  function handleWayPointChange(index, place) {
    const newWayPoints = [...search.wayPoints];
    if (!place && search.wayPoints.length > 2) newWayPoints.splice(index, 1);else newWayPoints[index] = place;
    search.wayPoints = newWayPoints;
    setWayPoints(newWayPoints);
    updateWayPointsOnMap(newWayPoints);
    if (!search.computable && place) {
      const {
        point: {
          coordinates: [lng, lat]
        }
      } = place;
      map?.flyTo({
        center: {
          lat,
          lng
        },
        zoom: Math.max(15, map.getZoom())
      });
    }
    onUpdate?.();
    compute();
  }
  function handleReverse() {
    const newWayPoints = [...search.wayPoints];
    newWayPoints.reverse();
    search.wayPoints = newWayPoints;
    setWayPoints(newWayPoints);
    updateWayPointsOnMap(newWayPoints);
    onUpdate?.();
    compute();
  }
  function handleStepAdd() {
    const newWayPoints = [...search.wayPoints];
    const index = newStepPosition === 'before_last' ? search.wayPoints.length - 1 : search.wayPoints.length;
    newWayPoints.splice(index, 0, undefined);
    search.wayPoints = newWayPoints;
    setWayPoints(newWayPoints);
    setTimeout(() => document.getElementById(`way-point-${index}-autocomplete`)?.focus(), 200);
  }
  function handleWayPointItemDragEnd({
    source,
    destination
  }) {
    if (!destination || source.index === destination.index) return;
    const newWayPoints = [...search.wayPoints];
    const [movedWayPoint] = newWayPoints.splice(source.index, 1);
    newWayPoints.splice(destination.index, 0, movedWayPoint);
    search.wayPoints = newWayPoints;
    setWayPoints(newWayPoints);
    updateWayPointsOnMap(newWayPoints);
    onUpdate?.();
    compute();
  }
  const computable = Boolean(wayPoints[0]) && Boolean(wayPoints[1]);
  const newStepAdditionEnabled = !disableNewStepAddition && computable && Boolean(wayPoints[wayPoints.length - 1]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Box, null, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    gap: 2
  }, /*#__PURE__*/React.createElement(DragDropContext, {
    onDragEnd: handleWayPointItemDragEnd
  }, /*#__PURE__*/React.createElement(Droppable, {
    droppableId: "droppable"
  }, droppableProvided => /*#__PURE__*/React.createElement(_Box, _extends({
    flexGrow: 1
  }, droppableProvided.droppableProps, {
    marginLeft: "-20px",
    ref: droppableProvided.innerRef
  }), /*#__PURE__*/React.createElement(WayPointsList, {
    draggable: true
  }, wayPoints.map((_, index) => /*#__PURE__*/React.createElement(Fragment, {
    key: index
  }, /*#__PURE__*/React.createElement(Draggable, {
    draggableId: `${index}`,
    index: index,
    isDragDisabled: readOnly
  }, draggableProvided => /*#__PURE__*/React.createElement("div", _extends({
    ref: draggableProvided.innerRef
  }, draggableProvided.draggableProps, draggableProvided.dragHandleProps), /*#__PURE__*/React.createElement(WayPointRow, {
    draggable: true,
    defaultOptions: defaultOptions,
    geolocation: geolocation,
    index: index,
    map: map,
    mapClickDisabled: mapClickDisabled,
    mapHasContextMenu: Boolean(onContextMenuOpen),
    onBlur: handleWayPointBlur,
    onChange: handleWayPointChange,
    readonly: readOnly,
    size: size,
    userPlaces: userPlaces,
    wayPoints: wayPoints
  }))))), droppableProvided.placeholder)))), /*#__PURE__*/React.createElement(_Box, {
    alignSelf: "stretch",
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    gap: 2,
    justifyContent: "space-between",
    paddingY: 1,
    width: 30
  }, /*#__PURE__*/React.createElement(_Tooltip, {
    placement: "left",
    title: t('commons.routing.actions.swap') || ''
  }, /*#__PURE__*/React.createElement(_IconButton, {
    disabled: readOnly,
    onClick: handleReverse,
    size: "small"
  }, /*#__PURE__*/React.createElement(SwapIcon, {
    fontSize: "small"
  }))), newStepAdditionEnabled && /*#__PURE__*/React.createElement(_Tooltip, {
    placement: "left",
    title: t('commons.routing.actions.add_step', {
      context: newStepPosition === 'before_last' ? 'before_last' : ''
    }) || ''
  }, /*#__PURE__*/React.createElement(_IconButton, {
    disabled: readOnly,
    onClick: handleStepAdd,
    size: "small"
  }, /*#__PURE__*/React.createElement(_Add, {
    fontSize: "small"
  })))))), (!disableBikeType || !disableEBike) && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "stretch",
    display: "flex",
    flexDirection: "column",
    gap: 1,
    marginTop: 3
  }, !disableBikeType && /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 1
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 600
  }, t('commons.routing.form.bike_type')), /*#__PURE__*/React.createElement(_FormControl, {
    fullWidth: true,
    color: "secondary",
    size: "small",
    sx: {
      minWidth: 180
    }
  }, /*#__PURE__*/React.createElement(_Select, {
    color: "primary",
    inputProps: {
      sx: {
        alignItems: 'center',
        display: 'flex',
        gap: 1
      }
    },
    onChange: handleBikeTypeChange,
    sx: {
      borderRadius: '10px'
    },
    value: bikeType
  }, bikeTypes.map(({
    key,
    Icon,
    labelKey,
    status
  }) => /*#__PURE__*/React.createElement(_MenuItem, {
    disabled: status === 'comingSoon',
    key: key,
    sx: theme => ({
      '&&': {
        alignItems: 'center',
        display: 'flex',
        color: status === 'comingSoon' ? theme.palette.text.secondary : 'inherit',
        gap: 1,
        opacity: 1,
        '&.Mui-selected': {
          backgroundColor: lighten(theme.palette.primary.main, 0.9)
        },
        '&:hover': {
          backgroundColor: lighten(theme.palette.primary.main, 0.8)
        }
      }
    }),
    value: key
  }, /*#__PURE__*/React.createElement(Icon, null), /*#__PURE__*/React.createElement(Trans, {
    i18nKey: labelKey
  }), status && /*#__PURE__*/React.createElement(_Chip, {
    color: "secondary",
    label: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: status === 'comingSoon' ? 'commons.routing.form.coming_soon' : 'commons.routing.form.beta'
    }),
    size: "small",
    sx: {
      cursor: 'inherit'
    }
  })))))), !disableEBike && /*#__PURE__*/React.createElement(_FormControlLabel, {
    control: /*#__PURE__*/React.createElement(_Checkbox, {
      checked: eBikeEnabled,
      color: "primary",
      onChange: handleEBikeToggle,
      sx: {
        marginRight: 1,
        padding: '4px'
      }
    }),
    label: t('commons.routing.form.e_bike'),
    sx: {
      margin: 0
    }
  }), !disableBikeType && pavedOnlyBikeTypes.includes(bikeType) && /*#__PURE__*/React.createElement(_FormControlLabel, {
    control: /*#__PURE__*/React.createElement(_Checkbox, {
      checked: asphaltOnly,
      color: "primary",
      onChange: handleAsphaltOnlyToggle,
      sx: {
        marginRight: 1,
        padding: '4px'
      }
    }),
    label: t('commons.routing.form.asphalt_only'),
    sx: {
      margin: 0
    }
  })));
}
export const RoutingForm = /*#__PURE__*/forwardRef(_RoutingForm);